import React, { memo, useRef, useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'

import styles from './Item.module.scss'
import useMenu from '../context/hooks/useMenu'

import { addPaddingAndScrollUpToElement, getElementScrollPosition, ScrollUpToElement, scrollingHasStopped } from '../utils/search'
import { ButtonBase } from '@material-ui/core'
import useCartItem from '../VNItems/hooks/useCartItem'
import VNMenuQuantity from '../VNItems/components/VNMenuQuantity'

/**
 * @todo - once category navigation is redone, remove this component in favor of VNMenuItem
 */
const Item = ({
  id,
  name,
  price,
  categoryId,
  cartIndex,
  emojis,
  status,
  maxReached,
  inventory,
  cartQuantity,
  cartQuantityWithModifiers,
  color,
  onTap,
  orderMaxAmount: max,
  orderMinCount: min,
  isAlcohol,
  modifierGroups,
  hasRequiredModifiers = false,
  className,
  highlighted,
  focusedItem,
  categoryBreakPoints,
}) => {
  const available = status === 'available'
  const itemRef = useRef()
  let classNames = cn(styles.item, className)
  let style = { backgroundColor: color }
  let _price = price
  const { scrollPositions, setScrollPositions, selectedCategory, setSelectedCategory } = useMenu()
  const throttlingRef = useRef(null)
  const throttlingActiveRef = useRef(false) // checks if in middle of throttle timer
  const isInCartRef = useRef(cartQuantity > 0)
  const latestQuantityRef = useRef(cartQuantity)
  const [localQuantity, setLocalQuantity] = useState(cartQuantity)

  const { addItemWithModifiers, upsertItem } = useCartItem({
    throttlingRef,
    throttlingActiveRef,
    isInCartRef,
    latestQuantityRef,
  })

  if (inventory <= 10) {
    price = `${price} - ${inventory} LEFT!`
  }

  if (maxReached) {
    style.opacity = 0.6
    _price = 'MAX REACHED'
  }

  if (!available) {
    classNames = cn(classNames, styles.unavailable)
    style = {}
    _price = 'SOLD OUT'
  }

  if (!highlighted) {
    style.opacity = '.1'
  }

  if (focusedItem) {
    classNames = cn(classNames, styles.active)
  }

  useEffect(() => {
    if (focusedItem && itemRef.current) {
      let setIntervalTimer
      if (!selectedCategory.search) {
        addPaddingAndScrollUpToElement(itemRef.current)
        setIntervalTimer = 10
      }
      if (categoryBreakPoints[categoryId] === id && selectedCategory.search) {
        ScrollUpToElement(itemRef.current)
        setIntervalTimer = 100
      }
      const scrollTimer = setInterval(() => {
        if (!itemRef.current || scrollingHasStopped(itemRef.current)) {
          setSelectedCategory({ id: categoryId, search: false, highlight: true })
          clearInterval(scrollTimer)
        }
      }, setIntervalTimer)
      setTimeout(() => {
        clearInterval(scrollTimer)
      }, 1000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedItem])

  useEffect(() => {
    if (itemRef.current && !scrollPositions[categoryId] && categoryBreakPoints[categoryId] === id) {
      const scrollPosition = getElementScrollPosition(itemRef.current)
      setScrollPositions({ [categoryId]: scrollPosition, ...scrollPositions })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryBreakPoints, scrollPositions])

  // resets to cart quantity when cart quantity changes outside of component
  useEffect(() => {
    if (throttlingActiveRef.current) return
    if (cartQuantity === localQuantity) return

    isInCartRef.current = cartQuantity > 0
    latestQuantityRef.current = cartQuantity
    setLocalQuantity(cartQuantity)
  }, [cartQuantity, localQuantity])

  // Add item to cart with throttling functionality
  const handleOnTap = useCallback(() => {
    if (localQuantity === max) return

    // Update local quantity immediately
    if (localQuantity === 0) {
      setLocalQuantity(min || 1)
      latestQuantityRef.current = min || 1
    } else {
      setLocalQuantity((prevQuantity) => prevQuantity + 1)
      latestQuantityRef.current++
    }

    if (hasRequiredModifiers) {
      addItemWithModifiers({ itemId: id, isAlcohol })
      return
    }

    upsertItem({
      itemId: id,
      isAlcohol,
      hasRequiredModifiers,
      max,
      cartIndex,
    })
  }, [addItemWithModifiers, cartIndex, hasRequiredModifiers, id, isAlcohol, localQuantity, max, min, upsertItem])

  return (
    <ButtonBase component="div" className={classNames} ref={itemRef} style={style} onClick={handleOnTap}>
      <div className={styles.title}>
        <span className={styles.name}>{name}</span>
      </div>
      <div className={styles.details}>
        <div className={styles.inventoryPrice}>
          <span className={styles.price}>{_price}</span>
        </div>
        <VNMenuQuantity color={color} value={localQuantity + cartQuantityWithModifiers} />
      </div>
    </ButtonBase>
  )
}

Item.defaultProps = {
  status: 'available',
  maxReached: false,
  emojis: [],
}

Item.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  price: PropTypes.string,
  emojis: PropTypes.array,
  status: PropTypes.oneOf(['unavailable', 'available']),
  maxReached: PropTypes.bool,
  inventory: PropTypes.number,
  cartQuantity: PropTypes.number,
  color: PropTypes.string,
  onTap: PropTypes.func,
  className: PropTypes.string,
  highlighted: PropTypes.bool,
  focusedItem: PropTypes.bool,
}

export default memo(Item)
